import React, { useState } from 'react';
import {
  AlignedInfo,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  ErrorHandler,
  Gap,
  Gutter,
  StatusPill,
  Tag,
  Text,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { todayIso } from '@common/services/datetime';
import {
  LOAD_BALANCING_WORKER_QUERY,
  type LoadBalancingWorkerType,
} from '@load-balancing/graphql/queries/load-balancing-worker';
import { SERVICE_LINE_TEAM_FILTERS_QUERY } from '@load-balancing/graphql';
import {
  employmentLabel,
  fteHoursLabel,
  shiftPatternLabel,
  shiftPreferenceLabel,
  toTeamReviewWorker,
} from '@load-balancing/services';
import {
  ScheduleWorkerProfileUpdateForm,
  useScheduleWorkerProfileUpdateForm,
  ScheduleWorkerUpcomingChanges,
} from '@load-balancing/components';

/**
 * View-only worker profile drawer for the Service Line Team Review page.
 *
 * Registered as the `schedule-worker-view-drawer` global dialog (URL-managed):
 * `open` / `onClose` are injected by the dialogs controller and `workerId` /
 * `schedulingPeriodId` come from the query params. `schedulingPeriodId` drives the
 * FTE-hours figure (40 × FTE% × period weeks). The drawer is a worker-profile view, so it
 * anchors on today: the "Profile" section shows the term in effect now and "Upcoming
 * Changes" lists every future-dated scheduled change. All params are optional so a
 * stale/missing one never throws — the drawer renders a skeleton, then empty until the
 * worker resolves.
 */
export function ScheduleTeamReviewWorkerDrawer({
  open,
  onClose,
  workerId,
  serviceLineId,
  schedulingPeriodId,
}: {
  open: boolean;
  onClose: () => void;
  workerId?: string;
  // Reviewed service line (from the dialog URL); scopes the profile form's Role options.
  serviceLineId?: string;
  schedulingPeriodId?: string;
  asOf?: string;
}) {
  // The drawer doubles as the "Update Profile" host (WORK-6258): tapping Update Profile (or
  // Edit on an upcoming change) swaps the view for the shared edit form; Cancel/Save/close
  // return to the view. `editTarget` is 'new' to create, a term uuid to edit, or null.
  const [editTarget, setEditTarget] = useState<string | 'new' | null>(null);

  const { data, loading, error } = useQuery(LOAD_BALANCING_WORKER_QUERY, {
    variables: { id: workerId ?? '', schedulingPeriodId },
    skip: !workerId,
  });

  // Service line roles for the picker. cache-first, so this reuses what the team-review page already
  // loaded for its Role filter (same query + serviceLineId) — no extra request when the cache is warm.
  const { data: filtersData, error: filtersError } = useQuery(
    SERVICE_LINE_TEAM_FILTERS_QUERY,
    {
      variables: { serviceLineId: serviceLineId ?? '' },
      skip: !serviceLineId,
    }
  );
  const assignableRoles = filtersData?.serviceLineTeamFilters?.roles ?? [];
  // The service line's charge role (data-driven): the profile form hides "Charge eligible"
  // when the selected role IS the charge role, never assuming it's literally "Charge".
  const chargeRole =
    filtersData?.serviceLineTeamFilters?.chargeRole ?? undefined;

  if (error || filtersError) {
    return (
      <ErrorHandler
        error={error || filtersError}
        log="ScheduleTeamReviewWorkerDrawer"
        messageType="contextual"
      />
    );
  }

  // A worker-profile view is anchored on today: the term in effect now is "current" and any
  // later-dated term is an "upcoming change". (schedulingPeriodId still drives FTE hours.)
  const asOf = todayIso();
  const loadBalancingWorker = data?.loadBalancingWorker;

  const handleClose = () => {
    setEditTarget(null);
    onClose();
  };

  if (editTarget && loadBalancingWorker) {
    return (
      <Drawer open={open} onClose={handleClose} title="Update Profile">
        <WorkerProfileEditPanel
          worker={loadBalancingWorker}
          asOf={asOf}
          schedulingPeriodId={schedulingPeriodId}
          assignableRoles={assignableRoles}
          chargeRole={chargeRole}
          editingChangeId={editTarget === 'new' ? null : editTarget}
          onDone={() => setEditTarget(null)}
        />
      </Drawer>
    );
  }

  const worker = loadBalancingWorker
    ? toTeamReviewWorker(loadBalancingWorker, asOf)
    : undefined;

  const chargeEligible = worker?.chargeEligible ? 'Yes' : '—';

  const statusTags = worker && (
    <StatusTags
      key="status"
      status={worker.status}
      employmentLabel={employmentLabel(worker.employmentType)}
    />
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      loading={loading}
      title={
        worker ? <Text transform="capitalize">{worker.name}</Text> : undefined
      }
      footer={
        worker && (
          <Button kind="primary" onClick={() => setEditTarget('new')}>
            Update Profile
          </Button>
        )
      }
    >
      {worker && (
        <>
          <Gutter direction="bottom" size="sm">
            <Text size="lg" weight="medium" block>
              Profile
            </Text>
          </Gutter>
          <AlignedInfo
            fullWidth
            content={[
              ['Status', statusTags],
              ['Unit', worker.unit ?? '—'],
              ['Role', worker.role ?? '—'],
              ['Charge Eligible', chargeEligible],
              ['FTE Hours', fteHoursLabel(worker.fteHours)],
            ]}
          />

          <Divider secondary gutter={24} />

          <Gutter direction="bottom" size="sm">
            <Text size="lg" weight="medium" block>
              Preferences
            </Text>
          </Gutter>
          <AlignedInfo
            fullWidth
            content={[
              ['Shift', shiftPreferenceLabel(worker.shiftType)],
              ['Shift Pattern', shiftPatternLabel(worker.shiftPattern)],
            ]}
          />

          <Divider secondary gutter={24} />

          <Gutter direction="bottom" size="md">
            <Text size="lg" weight="medium" block>
              Upcoming Changes
            </Text>
          </Gutter>
          <ScheduleWorkerUpcomingChanges
            changes={worker.upcomingChanges}
            workerId={workerId ?? ''}
            schedulingPeriodId={schedulingPeriodId}
            onEdit={setEditTarget}
          />
        </>
      )}
    </Drawer>
  );
}

// Edit page for the drawer: mounted only while editing, so the shared form (and its hook)
// initialise from the worker's current term history. Owns its Cancel/Save controls.
function WorkerProfileEditPanel({
  worker,
  asOf,
  schedulingPeriodId,
  assignableRoles,
  chargeRole,
  editingChangeId,
  onDone,
}: {
  worker: LoadBalancingWorkerType;
  asOf: string;
  schedulingPeriodId?: string;
  assignableRoles: readonly string[];
  chargeRole?: string;
  editingChangeId: string | null;
  onDone: () => void;
}) {
  const form = useScheduleWorkerProfileUpdateForm({
    workerId: worker.id,
    terms: worker.terms,
    asOf,
    schedulingPeriodId,
    editingTermId: editingChangeId,
    onSaved: onDone,
  });

  return (
    <Gap vertical size="lg" align="stretch">
      <ScheduleWorkerProfileUpdateForm
        values={form.values}
        onChange={form.update}
        saveAttempted={form.saveAttempted}
        serverErrors={form.serverErrors}
        datesDisabled={editingChangeId !== null}
        assignableRoles={assignableRoles}
        chargeRole={chargeRole}
      />
      <ButtonGroup align="right">
        <Button kind="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button kind="primary" loading={form.saving} onClick={form.save}>
          Save
        </Button>
      </ButtonGroup>
    </Gap>
  );
}

function StatusTags({
  status,
  employmentLabel: employment,
}: {
  status: 'active' | 'inactive';
  employmentLabel: string;
}) {
  return (
    <Gap key="status" size="xs" align="center">
      <StatusPill variant={status === 'active' ? 'success' : 'neutral'}>
        <Text weight="medium" transform="capitalize">
          {status}
        </Text>
      </StatusPill>
      <Tag color="steelLighter" kind="square">
        <Text weight="medium">{employment}</Text>
      </Tag>
    </Gap>
  );
}
